import {createSlice} from "@reduxjs/toolkit";
import _ from "lodash";
import moment from 'moment';
import API from "../../app/api";
import Formatter from '../../util/formatter';
import Notes from '../../util/notes';
import {addNoteForPatient} from './notes';
import {getAlarmBase} from '../../pages/ward/mainScreen/util/getAlarmBase';
import {checkHospitalId} from '../../pages/ward/attendanceCollection';
import {setSnackbar} from './globals';
import {getConfig} from '../../util/configUtil';
import { setCounterVisitsED, setCounterVisitsIP } from "./patientContext";

export const additionalDataSlice = createSlice({
  name: "additionalData",
  initialState: {
    value: {
      other: {}
    },
    patientHistoryActivityData: [],
    loading: undefined,
    loadingHistorical: undefined,
  },
  reducers: {
    setPatientAdditionalData: (state, patient) => {
      state.value = patient.payload;
    },
    setAdditionalData: (state, patient) => {
      state.value.other = patient.payload;
    },
    setAdditionalDataId: (state, patient) => {
      state.value.__id = patient.payload;
    },
    setLoading: (state, patient) => {
      state.loading = patient.payload;
    },
    setLoadingHistorical: (state, patient) => {
      state.loadingHistorical = patient.payload;
    },
    setLoadingPref: (state, patient) => {
      state.loadingPref = patient.payload;
    },
    setPatientHistoryActivityData: (state, patient) => {
      state.patientHistoryActivityData = patient.payload;
    },
  },
});

export const {
  setAdditionalData,
  setPatientAdditionalData,
  setAdditionalDataId,
  setLoading,
  setLoadingHistorical,
  setLoadingPref,
  setPatientHistoryActivityData,
} = additionalDataSlice.actions;

export const additionalData = state => state.additionalData.value;
export const loading = state => state.additionalData.loading;
export const loadingPref = state => state.additionalData.loadingPref;
export const patientHistoryData = state => state.additionalData.patientHistoryActivityData;

export const loadPatientAdditionalData = patientId => (dispatch, getState) => {
  if (getState().additionalData.loading !== patientId) {
    dispatch(setLoading(patientId));
    API.getPatientAdditionalData(patientId).then(data => {
      if (getState().additionalData.loading === patientId) {
        dispatch(data !== "" && data.other !== null ? setPatientAdditionalData(data) : setPatientAdditionalData({other: {}}));
      }
      dispatch(setLoading(undefined));
    }).catch(function (error) {
      console.error(error);
    });
  }
};

export const loadPatientHistoricalData = (patient) => (dispatch, getState) => {
  const customMap = getConfig('modules.map.customMap', false);

  if (getState().additionalData.loadingHistorical !== patient?.patient?.__id) {
    dispatch(setLoadingHistorical(patient?.patient?.__id));
    dispatch(setCounterVisitsED(null));
    dispatch(setCounterVisitsIP(null));
    API.getPatientAttendancesHistory(patient?.patient?.__id)
        .then((result) => {
          dispatch(setPatientHistoryActivityData(result));
          const tempArrED = [];
          const tempArrIP = [];
          const date = moment().subtract({year: 1});
          _.forEach(result, att => {
            if (moment(date).isSameOrBefore(att.admission.startDate) && att.__id !== patient.__id) {
              if (att?.admission?.attendanceType === 'E') tempArrED.push(att);
              else if (att?.admission?.attendanceType === 'I') tempArrIP.push(att);
            }
          });

          if (customMap) {
            const filter = `hospitalNumber eq '${patient?.patient?.__id}' OR hospitalNumber eq '${checkHospitalId(patient?.patient?.__id)}'`;
            API.getDictByURL(`/$dict/UHDAttendances?$filter=${filter}`)
                .then(r => {
                  const oneYearAgo = moment().subtract(1, 'year');
                  const filtered = _.filter(r?.__data, att => att?.arrivalDate && moment(att.arrivalDate).isBetween(oneYearAgo, moment()));
                  dispatch(setCounterVisitsED(tempArrED.length + filtered.length));
                });
          } else {
            dispatch(setCounterVisitsED(tempArrED.length));
            dispatch(setCounterVisitsIP(tempArrIP.length));
          }
        })
        .catch(e => {
          dispatch(setCounterVisitsED('-'));
          dispatch(setCounterVisitsIP('-'));
          dispatch(setPatientHistoryActivityData([]));
          console.error(e);
          dispatch(setSnackbar({type: 'error', message: 'Unable to retrieve recent visits'}));
        })
        .finally(() => {
          dispatch(setLoadingHistorical(undefined));
        })
  }
};

export const unloadAdditionalData = () => (dispatch, getState) => {
  dispatch(setPatientAdditionalData({other: {}}));
};

export const updatePatientAdditionalData = (patientId, data) => (dispatch, getState) => {
  let cloneOther = {};
  dispatch(setLoading(patientId));
  if (getState().additionalData.value && getState().additionalData.value.other) {
    if (getState().additionalData.value.__id === patientId) {
      cloneOther = _.cloneDeep(getState().additionalData.value.other);
    }
  }
  cloneOther = {...cloneOther, ...data};
  API.updatePatientAdditionalData(patientId, cloneOther)
      .then(response => {
        if (response.data === true) {
          dispatch(setAdditionalData(cloneOther));
          dispatch(setAdditionalDataId(patientId));
          dispatch(setLoading(undefined));
        } else {

        }
      })
      .catch(error => {
        console.error(error);
        dispatch(setSnackbar({type: 'error', message: 'Failed to save data.'}));
      })
};

export const insertNewAlert = (type, spellId, ptId, user, episodeId = null) => (dispatch, getState) => {
  let getBase = getAlarmBase(false);
  const userFullName = Formatter.formatUserFullName(user);
  const currentDate = moment();
  const expireDate = moment().add(1, 'week');

  let alerts = _.cloneDeep(getState().additionalData?.value?.other?.alarms) || [];
  let edited = false;

  switch (type) {
    case 'cfsOver6': {
      const findIndex = _.findIndex(alerts, a => a?.cfsOver6 && !a.deleted && moment(a.expireDate).isSameOrAfter(moment()));
      if (findIndex === -1) {
        getBase['cfsOver6'] = true;
        getBase['group'] = 'CLINICAL';
        getBase['name'] = 'Clinical Frailty Score is 6 or more';
        getBase['description'] = 'Clinical Frailty Score indicates Social History and Medication Review completion required and/or refer to acute frailty service';
        getBase['userAdd'] = userFullName;
        getBase['userAddId'] = user?.__id;
        getBase['userAddLogin'] = user?.userName;
        getBase['dateAdd'] = currentDate.valueOf();
        getBase['expireDate'] = expireDate.valueOf();
        getBase['acknowledgeBy'] = [{
          user: user?.userName,
          timestamp: currentDate.toISOString(),
        }];
        alerts.push(getBase);
        edited = true;
      }
      break;
    }

    case 'cancelCfsOver6': {
      const findIndex = _.findIndex(alerts, a => a?.cfsOver6 && !a.deleted && moment(a.expireDate).isSameOrAfter(moment()));
      if (findIndex !== -1) {
        let alert = alerts[findIndex];
        alert['deleted'] = true;
        alert['userDelete'] = userFullName;
        alert['userDeleteLogin'] = user?.userName;
        alert['userDeleteId'] = user?.__id;
        alert['dateDelete'] = currentDate.valueOf();
        edited = true;
      }
      break;
    }

    default:
      console.warn('Type no implemented', type);
  }

  if (edited) {
    dispatch(updatePatientAdditionalData(ptId, {alarms: alerts}));
    dispatch(addNoteForPatient(ptId, {
      note: Notes.getPatientAlarmsNote(getBase),
      noteType: 'alarms',
      patientsInWardsRef: [spellId],
      episodeId: episodeId || null,
    }, spellId));
  }
};

export default additionalDataSlice.reducer;
